// ---------------------------------------------------------------------------
// What the ported components share (R7)
//
// The same helpers as the design system's components/bundle.js, so a port
// formats, names and moves exactly as the system does: the class joiner, the
// reader's locale for numbers and dates, the words for a status the system
// does not list, and roving focus in a group of options.
//
// Three stand in for what React does by itself: node() draws a value the way
// React draws a child (R9), reactKey() reads a key the way React's handlers
// get it (R9; React's whole getEventKey since R10f), and reactStyle() sets a
// style prop the way React does, through the CSSOM (R10f).
// ---------------------------------------------------------------------------

import { noChange, nothing, type ReactiveControllerHost } from 'lit';
import { type AttributePart, Directive, directive, type PartInfo, PartType } from 'lit/directive.js';

/** The truthy parts, joined with spaces: cx('a', false && 'b', 'c') is 'a c'. */
export function cx(...parts: unknown[]): string {
  return parts.filter(Boolean).join(' ');
}

let LOCALE = 'en-US';

/** Numbers and dates in the reader's locale; set it once, before rendering (default en-US). */
export function setLocale(locale?: string): void {
  LOCALE = locale || 'en-US';
}

export function currentLocale(): string {
  return LOCALE;
}

/** A number with the locale's separators; anything else as it is. */
export function num(n: unknown): unknown {
  return typeof n === 'number' ? n.toLocaleString(LOCALE) : n;
}

function percent(n: number, digits?: number): string {
  const d = digits == null ? 1 : digits;
  return new Intl.NumberFormat(LOCALE, { style: 'percent', minimumFractionDigits: d, maximumFractionDigits: d }).format(
    n,
  );
}

const asDate = (d: Date | string | number) => (d instanceof Date ? d : new Date(d));

/** The system's formatter (SDR.format): numbers, percentages (0.064 → 6.4%), dates and times. */
export const format = {
  number: (n: number, opts?: Intl.NumberFormatOptions) => new Intl.NumberFormat(LOCALE, opts).format(n),
  percent,
  date: (d: Date | string | number, opts?: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(LOCALE, opts || { day: 'numeric', month: 'short', year: 'numeric' }).format(asDate(d)),
  time: (d: Date | string | number, opts?: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(LOCALE, opts || { hour: '2-digit', minute: '2-digit' }).format(asDate(d)),
};

/** waiting_for_review → "Waiting for review": a status the system does not know still says what it is. */
export function sentence(s: unknown): string {
  const words = String(s == null ? '' : s)
    .replace(/[_-]+/g, ' ')
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Unknown';
}

const STEP: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

/** Roving focus in a group of options: the index a key moves to (all four arrows, Home, End), or null. */
export function rovingIndex(key: string, index: number, length: number): number | null {
  if (Object.hasOwn(STEP, key)) return (index + (STEP[key] as number) + length) % length;
  return key === 'Home' ? 0 : key === 'End' ? length - 1 : null;
}

/** A value as React draws a child: true, false, null and undefined are nothing, in a list too; Lit would write "true". */
export const node = (v: unknown): unknown =>
  v == null || typeof v === 'boolean' ? nothing : Array.isArray(v) ? v.map(node) : v;

/**
 * Whether a node prop is there, as the system asks before it draws a wrapper (p.actions ? <div>…</div> : null),
 * R10d. A Lit app says "no content" with nothing where a React app says null; nothing is a truthy symbol, so it
 * counts as absent here. Anything else goes by its truthiness, as in React: an empty list is there, and the system
 * draws the wrapper around it.
 */
export const present = (v: unknown): boolean => !!v && v !== nothing;

/**
 * React's names for the key values older browsers report (react-dom 18.3.1, getEventKey's normalizeKey), all of
 * them. Looked up in a plain object, as React does.
 */
const NORMALIZE_KEY: Record<string, string> = {
  Esc: 'Escape',
  Spacebar: ' ',
  Left: 'ArrowLeft',
  Up: 'ArrowUp',
  Right: 'ArrowRight',
  Down: 'ArrowDown',
  Del: 'Delete',
  Win: 'OS',
  Menu: 'ContextMenu',
  Apps: 'ContextMenu',
  Scroll: 'ScrollLock',
  MozPrintableKey: 'Unidentified',
};

/**
 * A keydown or keyup that names no key goes by its keyCode (getEventKey's translateToKey, all of it): only the keys
 * every layout agrees on. Any other code is Unidentified.
 */
const TRANSLATE_TO_KEY: Record<number, string> = {
  8: 'Backspace',
  9: 'Tab',
  12: 'Clear',
  13: 'Enter',
  16: 'Shift',
  17: 'Control',
  18: 'Alt',
  19: 'Pause',
  20: 'CapsLock',
  27: 'Escape',
  32: ' ',
  33: 'PageUp',
  34: 'PageDown',
  35: 'End',
  36: 'Home',
  37: 'ArrowLeft',
  38: 'ArrowUp',
  39: 'ArrowRight',
  40: 'ArrowDown',
  45: 'Insert',
  46: 'Delete',
  112: 'F1',
  113: 'F2',
  114: 'F3',
  115: 'F4',
  116: 'F5',
  117: 'F6',
  118: 'F7',
  119: 'F8',
  120: 'F9',
  121: 'F10',
  122: 'F11',
  123: 'F12',
  144: 'NumLock',
  145: 'ScrollLock',
  224: 'Meta',
};

/** A keypress's character code as React reads it (getEventCharCode): Enter as 13, control characters as 0. */
function charCodeOf(e: KeyboardEvent): number {
  const keyCode = e.keyCode;
  let charCode = keyCode;
  if ('charCode' in e) {
    charCode = (e as KeyboardEvent & { charCode: number }).charCode;
    if (charCode === 0 && keyCode === 13) charCode = 13;
  }
  if (charCode === 10) charCode = 13;
  return charCode >= 32 || charCode === 13 ? charCode : 0;
}

/**
 * The key of a keyboard event as the system's handlers read it: React's getEventKey (react-dom 18.3.1), both tables
 * whole (R10f), so no port keeps a table of its own. The key the event names, by its new name; else a keypress by its
 * character, a keydown or keyup by its keyCode. tests/dom/shared-rules.dom.ts reads every entry beside React.
 */
export function reactKey(e: KeyboardEvent): string {
  if (e.key) {
    const key = NORMALIZE_KEY[e.key] || e.key;
    if (key !== 'Unidentified') return key;
  }
  if (e.type === 'keypress') {
    const c = charCodeOf(e);
    return c === 13 ? 'Enter' : String.fromCharCode(c);
  }
  if (e.type === 'keydown' || e.type === 'keyup') return TRANSLATE_TO_KEY[e.keyCode] || 'Unidentified';
  return '';
}

/** A style prop as React takes it: camelCase names (or --custom ones), numbers in px unless the property has none. */
export type ReactStyles = Record<string, string | number | boolean | null | undefined>;

/** React's properties whose numbers have no unit (react-dom 18.3.1, isUnitlessNumber), with its vendor prefixes. */
const UNITLESS = new Set(
  [
    'animationIterationCount',
    'aspectRatio',
    'borderImageOutset',
    'borderImageSlice',
    'borderImageWidth',
    'boxFlex',
    'boxFlexGroup',
    'boxOrdinalGroup',
    'columnCount',
    'columns',
    'flex',
    'flexGrow',
    'flexPositive',
    'flexShrink',
    'flexNegative',
    'flexOrder',
    'gridArea',
    'gridRow',
    'gridRowEnd',
    'gridRowSpan',
    'gridRowStart',
    'gridColumn',
    'gridColumnEnd',
    'gridColumnSpan',
    'gridColumnStart',
    'fontWeight',
    'lineClamp',
    'lineHeight',
    'opacity',
    'order',
    'orphans',
    'tabSize',
    'widows',
    'zIndex',
    'zoom',
    'fillOpacity',
    'floodOpacity',
    'stopOpacity',
    'strokeDasharray',
    'strokeDashoffset',
    'strokeMiterlimit',
    'strokeOpacity',
    'strokeWidth',
  ].flatMap(name => [name, ...['Webkit', 'ms', 'Moz', 'O'].map(p => p + name.charAt(0).toUpperCase() + name.slice(1))]),
);

/** A style value as React writes it (dangerousStyleValue): '' for none, a number in px unless unitless or 0. */
function styleValue(name: string, value: unknown): string {
  if (value == null || typeof value === 'boolean' || value === '') return '';
  if (!name.startsWith('--') && typeof value === 'number' && value !== 0 && !UNITLESS.has(name)) return `${value}px`;
  return String(value).trim();
}

/**
 * React's style prop (R10f): `style=${reactStyle({ minHeight: total || undefined })}`, the system's object as it
 * stands in bundle.js. In the page it does what React's client does: each property set on the element's style (the
 * CSSOM, which writes numbers as the browser serialises them), only those that changed since the last render, and
 * one no longer given emptied, so every other declaration on the element (a page's own, one an effect set) stays,
 * and the attribute stays too, as style="" in Chrome, when the last value goes. On the server (@lit-labs/ssr) it
 * writes what React's server writes: min-height:120px;left:0, no attribute when nothing is given. An attribute
 * binding writes the whole text instead, which loses all that.
 *
 * Every element port binds the style props the system sets live this way (the charts' plots, the donut's ring, the
 * tile's readout and axis, the DataTable's frame and headers); tests/dom/shared-rules.dom.ts holds them to it beside
 * the system. A template that is also compared on the server as text keeps its text (the skeleton, the meter), and
 * so does the DataTable's bar, whose max() happy-dom's CSSOM cannot hold, so no test could read it. The same file
 * counts every style binding of an element module that is not reactStyle, each with its reason.
 */
class ReactStyle extends Directive {
  private last: ReactStyles | null | undefined = undefined;

  constructor(part: PartInfo) {
    super(part);
    // Lit gives an attribute part its strings only when the binding is not the whole value: static text beside it
    // (style="color: red; ${…}") or another binding. Lit would then write that text with setAttribute on every
    // render, over what this directive set (R10f review), so both are refused, on the server too.
    if (
      part.type !== PartType.ATTRIBUTE ||
      part.name !== 'style' ||
      (part as PartInfo & { strings?: readonly string[] }).strings !== undefined
    )
      throw new Error('reactStyle is the whole value of a style attribute, the only binding in it');
  }

  /** React's server text (pushStyle). */
  render(styles: ReactStyles | null | undefined) {
    const out: string[] = [];
    for (const [name, value] of Object.entries(styles || {})) {
      if (value == null || typeof value === 'boolean' || value === '') continue;
      const css = name.startsWith('--')
        ? name
        : name
            .replace(/([A-Z])/g, '-$1')
            .toLowerCase()
            .replace(/^ms-/, '-ms-');
      out.push(`${css}:${styleValue(name, value)}`);
    }
    return out.length ? out.join(';') : nothing;
  }

  /** React's client (diffProperties, then setValueForStyles). */
  override update(part: AttributePart, [styles]: [ReactStyles | null | undefined]) {
    const last = this.last;
    if (styles === last && last !== undefined) return noChange;
    const style = (part.element as HTMLElement).style;
    const set = (name: string, value: unknown) => {
      const v = styleValue(name, value);
      if (name.startsWith('--')) style.setProperty(name, v);
      else (style as unknown as Record<string, string>)[name === 'float' ? 'cssFloat' : name] = v;
    };
    const next = styles || {};
    if (last) for (const name of Object.keys(last)) if (!Object.hasOwn(next, name)) set(name, '');
    for (const name of Object.keys(next)) if (!last || last[name] !== next[name]) set(name, next[name]);
    this.last = styles;
    return noChange;
  }
}

/** React's style prop, as the whole value of a style attribute (see ReactStyle). */
export const reactStyle = directive(ReactStyle);

type Pending = Element & { isUpdatePending?: boolean; performUpdate?: () => void };

/**
 * The Lit elements inside `root` that have not drawn yet (their update waits for a microtask), drawn now (R10d).
 * React commits a whole tree before its effects run, so an effect that picks a first focus or measures the content
 * (a Dialog's first field, a Popover's size, a Tooltip's place) finds a Checkbox, a MultiSelect or a Menu inside
 * already drawn. A port calls this before such an effect. Lit supports the early flush (performUpdate on a pending
 * update); their own children follow, a few levels deep.
 */
export function drawInside(root: Element): void {
  for (let level = 0; level < 8; level++) {
    const pending = [...root.querySelectorAll<Pending>('*')].filter(
      el => el.isUpdatePending === true && typeof el.performUpdate === 'function',
    );
    if (!pending.length) return;
    for (const el of pending) el.performUpdate?.();
  }
}

/** The hosts noBox follows the hidden attribute of. */
const watched = new WeakSet<HTMLElement>();

/**
 * Follows the host's hidden attribute while it is connected, and lets go when it leaves the page: an observer that
 * never lets go keeps its host alive, with its whole tree, in a DOM that lists every observing one until
 * disconnect(). happy-dom does, and R10f's first noBox kept every element port the DOM suite ever drew until it ran
 * out of memory in CI (tests/dom/page-guard.ts names such an observer). A Lit host connects and disconnects its
 * controllers itself; one added while it is connected starts at once.
 */
function followHidden(host: HTMLElement): void {
  const observer = new MutationObserver(() => placeBox(host));
  const follow = () => observer.observe(host, { attributes: true, attributeFilter: ['hidden'] });
  const lit = host as HTMLElement & Partial<ReactiveControllerHost>;
  if (lit.addController) lit.addController({ hostConnected: follow, hostDisconnected: () => observer.disconnect() });
  else follow();
}

/** display: contents while the host is shown, no display of noBox's own while it is hidden; the page's own stays. */
function placeBox(host: HTMLElement): void {
  const own = host.style.display;
  if (own && own !== 'contents') return;
  const want = host.hidden ? '' : 'contents';
  if (own !== want) host.style.display = want;
}

/**
 * The element of a light-DOM port makes no box of its own (R10d): display: contents, unless the page set a display,
 * so the system's root is the flex or grid item, or sits in the line, where React puts it. Call it when the element
 * is connected. Chrome found the box three times: an inline box around the CharCount's counter (4px lower), the
 * MultiSelect as the grid item without the field's min-width 0 (past a phone's edge), the Switch in a flex column
 * (4px taller). `item` is the system root's own floor as an item (its min-width 0), set too unless the page set its
 * own, for a page that gives the element a box after all. tests/dom/hosts.dom.ts holds every element port to it.
 *
 * hidden hides the element, as it hides any element (R10f): while the host has the attribute, noBox writes no
 * display, so the browser's [hidden] { display: none } applies, and it follows the attribute as it comes and goes
 * (the system has no host: a React caller hides a component by not rendering it or by hiding an element of its own
 * around it, and the element is that element in a Lit caller's markup). An inline display: contents would win over
 * the rule. A display the page set itself (anything but contents) stays, hidden or not, as on any element.
 * tests/dom/shared-rules.dom.ts holds every element port to it.
 */
export function noBox(host: HTMLElement, item: { minWidth?: string; minHeight?: string } = {}): void {
  placeBox(host);
  if (!watched.has(host)) {
    watched.add(host);
    // The attribute changes after the element has connected (?hidden=${…}, el.hidden = true): heard before the next
    // paint, as a mutation record is delivered at the end of the task's microtasks. Back on the page, the call from
    // connectedCallback places it again, so a change while it was away counts too.
    followHidden(host);
  }
  if (item.minWidth != null && !host.style.minWidth) host.style.minWidth = item.minWidth;
  if (item.minHeight != null && !host.style.minHeight) host.style.minHeight = item.minHeight;
}

/**
 * Whether a port's click handler must do nothing because React's would not run (R10d): React never calls the
 * onClick of a disabled button, input, select or textarea (nor its onMouseDown, onMouseUp, onMouseMove,
 * onDoubleClick or onMouseEnter: shouldPreventMouseEvent). A browser drops a click on the disabled control itself,
 * but one that lands on an element inside it (an icon, the label's span, a spinner, the Switch's knob) may still
 * reach the control's listener, and does in happy-dom. `el` is the element whose handler it is, by default the
 * listener's currentTarget, so ask while the event is dispatched. aria-disabled is not disabled here, as in React.
 * tests/dom/shared-rules.dom.ts clicks inside every port's disabled buttons beside the system.
 */
export function clickOnDisabled(e: Event, el: EventTarget | null = e.currentTarget): boolean {
  const c = el as (Element & { disabled?: unknown }) | null;
  return !!c && c.disabled === true && /^(BUTTON|INPUT|SELECT|TEXTAREA)$/.test(c.tagName);
}
